// Copyright (c) 2025, Quantum Xchange, Inc.
'use strict'

import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { Modal, ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useDefaultGetQuery } from 'js/queries/defaultQueries'

const WaitModal = ({ waitEvent }) => {
    const { t } = useTranslation()
    const firstPingResponse = useRef()

    const { data: pingData, error: errorFetchingPing } = useDefaultGetQuery({
        url: '/systemManagement/ping',
        useQueryParams: { refetchInterval: 2000 },
        requestParams: { customError: true },
    })

    useEffect(() => {
        //The first ping can happen when the system has already shutdown or is still in the process of shutting down
        //Therefore set first ping response if ping request errored or returned successfully
        if (firstPingResponse.current === undefined && (pingData || errorFetchingPing)) {
            firstPingResponse.current = errorFetchingPing ? 'error' : pingData.startTime
            return
        }

        //Reload app if ping status changes from error to non-error and returns a startTime
        //OR ping startTime increased since first ping response (startTime represents when the web server started)
        if (
            (firstPingResponse.current === 'error' && !errorFetchingPing && pingData?.startTime) ||
            (!isNaN(firstPingResponse.current) && pingData.startTime > firstPingResponse.current)
        ) {
            location.reload()
        }
    }, [pingData, errorFetchingPing])

    const waitEventMessages = {
        powerOff: { animate: false, primaryMsg: t('wait_modal.system_not_running') },
        restartSystem: { animate: true, primaryMsg: t('wait_modal.system_restarting'), secondaryMsg: t('wait_modal.wait_while_restarting') },
        restartWebServer: { animate: true, primaryMsg: t('wait_modal.web_server_restarting'), secondaryMsg: t('wait_modal.wait_while_restarting') },
    }

    return (
        <Modal show={true} backdrop="static" keyboard={false}>
            <Modal.Body className="text-center p-4">
                {waitEventMessages[waitEvent].primaryMsg && <h3 className="mb-3">{waitEventMessages[waitEvent].primaryMsg}</h3>}
                {waitEventMessages[waitEvent].animate && <FixedHeightProgressBar striped animated now={100} className="mb-3 ms-5 me-5" />}
                {waitEventMessages[waitEvent].secondaryMsg && <div>{waitEventMessages[waitEvent].secondaryMsg}</div>}
            </Modal.Body>
        </Modal>
    )
}

WaitModal.propTypes = {
    waitEvent: PropTypes.oneOf(['powerOff', 'restartSystem', 'restartWebServer']).isRequired,
}

export default WaitModal

//Styled
const FixedHeightProgressBar = styled(ProgressBar)`
    height: 25px !important;
`
